import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/config";
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, Shield, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import PWAInstallButton from "@/components/PWAInstallButton";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/entreprise", label: "Entreprises" },
  { to: "/candidats", label: "Candidats" },
  { to: "/avantages", label: "Avantages" },
  { to: "/offres", label: "Offres" },
  { to: "/actualites", label: "Actualités" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user || user.role === "admin") return;
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.count);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="navbar" className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_c50239db-e303-4ac2-9fad-ed65c9848429/artifacts/l9apbe3g_LOGO-FR-INTERIM.png"
              alt="FR INTERIM"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? "text-[#662483] border-b-2 border-[#662483]"
                    : "text-[#3F3F3F] hover:text-[#662483]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-3">
            <PWAInstallButton variant="navbar" />
            {user ? (
              <>
                {/* Notification Bell - only for candidates/employees */}
                {user.role !== "admin" && (
                  <Link to="/dashboard" className="relative p-2 text-[#3F3F3F] hover:text-[#662483] transition-colors" data-testid="notification-bell">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Link>
                )}
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-[#3F3F3F]" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#662483] to-[#0069B4] flex items-center justify-center text-white text-xs font-bold">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <span className="text-sm font-medium">{user.name?.split(" ")[0]}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {user.role === "admin" ? (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin-link">
                        <Shield className="w-4 h-4" /> Backoffice
                      </Link>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-dashboard-link">
                        <LayoutDashboard className="w-4 h-4" /> Mon espace
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer" data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <Link to="/auth">
                <Button
                  className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold px-6 py-2 rounded-none hover:opacity-90 transition-opacity"
                  data-testid="nav-login-btn"
                >
                  Je m'inscris
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-[#3F3F3F]"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2" data-testid="mobile-menu">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`block px-4 py-2 text-sm font-medium rounded ${
                isActive(link.to) ? "bg-[#F0E9F3] text-[#662483]" : "text-[#3F3F3F] hover:bg-gray-50"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-gray-200">
            {user ? (
              <>
                <Link
                  to={user.role === "admin" ? "/admin" : "/dashboard"}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2 text-sm font-medium text-[#662483]"
                >
                  {user.role === "admin" ? "Backoffice" : "Mon espace"}
                </Link>
                <button
                  onClick={() => { logout(); setMobileOpen(false); }}
                  className="block w-full text-left px-4 py-2 text-sm font-medium text-red-600"
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setMobileOpen(false)}>
                <Button className="w-full bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold rounded-none">
                  Je m'inscris
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
